import { MOCK_CONVERSATION_MESSAGES } from '../../constants/chat';
import { HTTPMethod } from '../../enums/request';
import {
    CreateConversationResponseSchema,
    MessageActionResponseSchema,
    GetConversationResponseSchema,
    GetUserConversationStateResponseSchema,
    MessageSchema,
    GetUserPermissionsResponseSchema,
    MessageAttachmentUploadUrlResponse,
    MessageAttachmentViewUrlResponse,
    MessageAttachmentScanFileResponse
} from '../../types/chat';
import { _callAuthenticatedAPI, mock } from '../apiCaller';

const WAGGLE_API_PREFIX = 'waggle';

export const fetchConversationMessages = (
    conversationId: number,
    page: number,
    resultsPerPage: number,
    entityId: string | null,
    query?: string
): Promise<GetConversationResponseSchema> => {
    if (mock) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const mockResponse: GetConversationResponseSchema = {
                    data: {
                        id: conversationId,
                        name: 'Mock Conversation',
                        messages: MOCK_CONVERSATION_MESSAGES,
                        nextCursor: false,
                        pageFull:
                            MOCK_CONVERSATION_MESSAGES.length >= resultsPerPage
                    },
                    status: 'ok'
                };

                resolve(mockResponse);
            }, 300);
        });
    } else {
        return new Promise((resolve) => {
            getConversation(
                conversationId,
                page,
                resultsPerPage,
                entityId,
                query
            ).then((response) => resolve(response));
        });
    }
};

export function createConversation(
    name: string,
    entityId: string,
    projectId: number,
    projectName: string,
    messages?: MessageSchema[]
): Promise<CreateConversationResponseSchema> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/conversation`,
        HTTPMethod.POST,
        {
            name,
            entityId,
            projectId,
            projectName,
            messages
        }
    );
}

export function getConversation(
    conversationId: number,
    page: number,
    resultsPerPage: number,
    entityId: string | null,
    query?: string
): Promise<GetConversationResponseSchema> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/conversation/${conversationId}?page=${page}&resultsPerPage=${resultsPerPage}&query=${query}&entityId=${entityId}`,
        HTTPMethod.GET
    );
}

export function getUserConversationState(
    entityId: string,
    resultsPerPage: number
): Promise<GetUserConversationStateResponseSchema> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/conversation/state/${entityId}?resultsPerPage=${resultsPerPage}`,
        HTTPMethod.GET
    );
}

export function sendMessage(
    conversationId: number,
    entityId: string,
    tagId: string,
    text: string,
    timestampEpochMs: number,
    replyMessageId?: number,
    attachmentIds?: number[]
): Promise<MessageActionResponseSchema> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/message`,
        HTTPMethod.POST,
        {
            conversationId,
            entityId,
            text,
            timestampEpochMs,
            replyMessageId,
            tagId,
            attachmentIds
        }
    );
}

export function deleteMessage(
    conversationId: number,
    messageId: number
): Promise<MessageActionResponseSchema> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/message`,
        HTTPMethod.DELETE,
        {
            conversationId,
            messageId
        }
    );
}

export function addReaction(
    conversationId: number,
    messageId: number,
    emoji: string
): Promise<any> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/message/reaction`,
        'POST',
        {
            conversationId,
            messageId,
            emoji
        }
    );
}

export function removeReaction(
    conversationId: number,
    messageId: number,
    emoji: string
): Promise<any> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/message/reaction`,
        'DELETE',
        {
            conversationId,
            messageId,
            emoji
        }
    );
}

export function getUserPermissions(): Promise<GetUserPermissionsResponseSchema> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/conversation/permission`,
        HTTPMethod.GET
    );
}

export function getMessageAttachmentUploadUrl(
    fileName: string,
    fileType: string,
    conversationId: number
): Promise<MessageAttachmentUploadUrlResponse> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/message/attachment/get-upload-url`,
        HTTPMethod.POST,
        { fileName, fileType, conversationId }
    );
}

export function getMessageAttachmentViewUrl(
    attachmentId: number
): Promise<MessageAttachmentViewUrlResponse> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/message/attachment/get-view-url/${attachmentId}`,
        HTTPMethod.GET
    );
}

export function scanMessageAttachment(
    attachmentId: number
): Promise<MessageAttachmentScanFileResponse> {
    return _callAuthenticatedAPI(
        `${WAGGLE_API_PREFIX}/api/v1/message/attachment/scan-file/${attachmentId}`,
        HTTPMethod.GET
    );
}
